import type { MessageInstance } from "antd/es/message/interface";
import type {
  IPublicClientApplication,
  AccountInfo,
} from "@azure/msal-browser";

export const handleMicrosoftSignIn = async (
  msal: any,
  setIsLoading: (loading: boolean) => void,
  message: MessageInstance,
) => {
  try {
    setIsLoading(true);
    message.loading("Redirecting to Microsoft...");
    await msal.login();
  } catch (error: any) {
    console.error("Microsoft login initiation failed:", error);
    message.error("Failed to initiate Microsoft login. Please try again.");
  } finally {
    setIsLoading(false);
    message.destroy();
  }
};

interface HandleMsalRedirectParams {
  instance: IPublicClientApplication;
  accounts: AccountInfo[];
  loginMicrosoft: (data: { token: string }) => { unwrap: () => Promise<any> };
  authComplete: (authData: any) => void;
  handleAuthError: (error: any) => void;
  navigate: (path: string, options?: { replace?: boolean }) => void;
  setIsProcessingRedirect: (processing: boolean) => void;
  message: MessageInstance;
}

export const handleMsalRedirectResponse = async ({
  instance,
  accounts,
  loginMicrosoft,
  authComplete,
  handleAuthError,
  navigate,
  setIsProcessingRedirect,
  message,
}: HandleMsalRedirectParams) => {
  try {
    const response = await instance.handleRedirectPromise();

    if (response) {
      // User successfully logged in with redirect
      message.loading("Completing sign in...", 0);
      const authData = await loginMicrosoft({
        token: response.accessToken,
      }).unwrap();
      message.destroy();
      authComplete(authData);
    } else if (accounts.length > 0) {
      // User is already logged in, get their token silently
      const account = accounts[0];
      message.loading("Verifying credentials...", 0);
      const tokenResponse = await instance.acquireTokenSilent({
        scopes: ["User.Read"],
        account,
      });
      const authData = await loginMicrosoft({
        token: tokenResponse.accessToken,
      }).unwrap();
      message.destroy();
      authComplete(authData);
    } else {
      // No authentication found
      setIsProcessingRedirect(false);
    }
  } catch (error: any) {
    console.error("Redirect response handling failed:", error);
    message.error(
      error?.data?.message ||
        error?.message ||
        "Authentication failed. Please try again.",
    );
    handleAuthError(error);
    setIsProcessingRedirect(false);
    navigate("/login", { replace: true });
  }
};
